import { EMBEDTYPES } from '@/utils/embeds/accountMeta'
import { CSS, HTML, JS } from '@/utils/embeds/defaultConfig'
import { DEVICES, JDOODLEBLOCKSNAMES, type IDevice } from '@/utils/embeds/editorMeta'
import { LANGUAGEMETA, type IMeta } from '@/utils/meta'
import { type Editor } from 'grapesjs'

import { languagesItems, type ILanguage } from '@/utils/sharedData/languages'
import { cloneDeep } from 'lodash-es'
import { defineStore } from 'pinia'
import { computed, reactive, ref } from 'vue'

import { useEmbedManagerStore } from '@/stores/embeds/embedManager.store'

export interface IEmbedLanguage {
  language: string
  versionIndex: number
  theme: string | null
  program: string | null
  libs: string[]
  projectKey?: string | number | null
}
export interface IConfigStyle {
  id: string
  value: any
}
export interface IComponentSettings {
  componentName: string
  configStyle: IConfigStyle[]
}
export interface IDefaultConfig {
  stdinArgs: string
  inputArgs: string
  compilerArgs: string
  interActiveMode: boolean
  fontSize: number
}
export interface IInputFiles {
  path: string
}
export interface IEmbedDraftConfig {
  languages: IEmbedLanguage[]
  config: {
    [key: string]: any
  }
  componentSettings: IComponentSettings[]
  defaultConfig: IDefaultConfig
  inputFiles: IInputFiles[]
}
export interface IEmbedConfigConig extends IEmbedDraftConfig {
  html: string
  css: string
  js: string
}
export interface IEmbed {
  id: string | null
  embedKey: string | null
  name: string
  description: string
  type: (typeof EMBEDTYPES)[keyof typeof EMBEDTYPES]
  draft: IEmbedDraftConfig | null
  config: IEmbedConfigConig
  savedAsDraft?: boolean | null
  activeStatus?: boolean | null
}

export const useEmbedConfigureStore = defineStore('embedConfigure', () => {
  const isEditorLoaded = ref<boolean>(false)
  const onEditMode = ref<boolean>(false)
  const isNewEmbed = ref<boolean>(true)

  const embed = reactive<IEmbed>({
    id: null,
    embedKey: null,
    name: '',
    description: '',
    type: EMBEDTYPES.BASIC,
    draft: null,
    config: {
      languages: [],
      config: {},
      componentSettings: [],
      defaultConfig: {
        stdinArgs: '',
        inputArgs: '',
        compilerArgs: '',
        interActiveMode: false,
        fontSize: 12
      },
      inputFiles: [],
      html: HTML,
      css: CSS,
      js: JS
    }
  })
  const htmlRef = ref<string>(HTML)
  const cssRef = ref<string>(CSS)
  const jsRef = ref<string>(JS)

  const currentEmbedsTypeCount = reactive<{
    [key: string]: number | null
  }>({
    [EMBEDTYPES.BASIC]: null,
    [EMBEDTYPES.PREMIUM]: null,
    [EMBEDTYPES.ULTRA]: null
  })

  const languages: ILanguage[] = cloneDeep(languagesItems)
    .sort((a: ILanguage, b: ILanguage) => (a.displayName > b.displayName ? 1 : -1))
    .filter((lang: ILanguage) => lang.isSupportedInAssignments == true)
  const languageMeta: IMeta = cloneDeep(LANGUAGEMETA)

  const canEdit = ref<boolean>(true)

  const sideMenuVisibility = ref<boolean>(true)
  const sideMenuStageId = ref<number>(-1)

  const editorInitiated = ref<boolean>(false)
  const editor = ref<Editor>({} as Editor)
  const editorConfig: any = {}

  const showStyleManager = ref<boolean>(false)
  const showCodeEditorStyleManager = ref<boolean>(false)
  const selectedStyleJdoodleBlock = ref<keyof typeof JDOODLEBLOCKSNAMES | null>(null)
  const selectedStyleBlock = ref<any | null>(null)
  const selectedDevice = ref<IDevice>(DEVICES[0])

  const getCanEdit = computed(() => {
    return canEdit.value
  })
  const getAccountType = computed(() => {
    return useEmbedManagerStore().getAccountType
  })
  const getIsEditorLoaded = computed(() => {
    return isEditorLoaded.value
  })
  const getOnEditMode = computed(() => {
    return onEditMode.value
  })
  const getIsNewEmbed = computed(() => {
    return isNewEmbed.value
  })
  const getEmbed = computed(() => {
    return embed
  })
  const getEmbedConfig = computed(() => {
    return getOnEditMode.value ? embed.draft : embed.config
  })
  const getComponentSettings = computed(() => {
    return getOnEditMode.value ? embed.draft?.componentSettings : embed.config.componentSettings
  })
  const getCurrentEmbedsTypeCount = computed(() => {
    return currentEmbedsTypeCount
  })
  const getIsBasicType = computed(() => {
    return getEmbed.value.type === EMBEDTYPES.BASIC
  })
  const getLanguages = computed(() => {
    return languages
  })
  const getLanguageMeta = computed(() => {
    return languageMeta
  })
  const getSideMenuVisibility = computed(() => {
    return sideMenuVisibility.value
  })
  const getSideMenuStageId = computed(() => {
    return sideMenuStageId.value
  })
  const getEditorInitiated = computed(() => {
    return editorInitiated.value
  })
  const getEditor = computed(() => {
    return editor.value
  })
  const getShowStyleManager = computed(() => {
    return showStyleManager.value
  })
  const getShowCodeEditorStyleManager = computed(() => {
    return showCodeEditorStyleManager.value
  })
  const getSelectedStyleJdoodleBlock = computed(() => {
    return selectedStyleJdoodleBlock.value
  })
  const getSelectedStyleBlock = computed(() => {
    return selectedStyleBlock.value
  })
  const getSelectedDevice = computed(() => {
    return selectedDevice.value
  })
  // sets

  // sets
  /**
   * Set the editor loaded
   * @param data - The editor loaded
   */
  const setIsEditorLoaded = (data: boolean) => {
    isEditorLoaded.value = data
  }
  /**
   * Set the can edit flag
   * @param data - The can edit flag
   */
  const setCanEdit = (data: boolean) => {
    canEdit.value = data
  }
  /**
   * Set the on edit mode
   * @param data - The on edit mode
   */
  const setOnEditMode = (data: boolean) => {
    onEditMode.value = data
  }
  /**
   * Set the is new embed
   * @param data - The is new embed
   */
  const setIsNewEmbed = (data: boolean) => {
    isNewEmbed.value = data
  }
  /**
   * Set the embed
   * @param data - The embed
   */
  const setEmbed = (data: IEmbed) => {
    embed.id = data?.id || null
    embed.embedKey = data?.embedKey || null
    embed.name = data?.name || ''
    embed.description = data?.description || ''
    embed.type = data?.type || EMBEDTYPES.BASIC
    embed.draft = data?.draft || null
    embed.config = data?.config || {
      languages: [],
      config: {},
      html: HTML,
      css: CSS,
      js: JS
    }
  }
  /**
   * Add a language to the embed
   * @param data - The language
   */
  const addLanguageToEmbed = (data: IEmbedLanguage) => {
    if (embed.draft === null) {
      embed.draft = cloneDeep(embed.config)
    }
    embed.draft.languages.push(data)
  }
  /**
   * Remove a language from the embed
   * @param data - The language
   */
  const removeLanguageFromEmbed = (data: IEmbedLanguage) => {
    if (embed.draft === null) {
      embed.draft = cloneDeep(embed.config)
    }
    embed.draft.languages = embed.draft.languages.filter(
      (lang: IEmbedLanguage) => lang.language !== data.language
    )
  }
  /**
   * Set the current embeds count
   * @param data - The embeds count
   */
  const setCurrentEmbedsTypeCount = (data: { [key: string]: number }) => {
    Object.keys(data).forEach((key) => {
      currentEmbedsTypeCount[key] = data[key]
    })
  }
  /**
   * Set the side menu visibility
   * @param data - The side menu visibility
   */
  const setSideMenuVisibility = (data: boolean) => {
    sideMenuVisibility.value = data
  }
  /**
   * Set the side menu stage
   * @param data - The side menu stage
   */
  const setSideMenuStageId = (data: number) => {
    sideMenuStageId.value = data
  }
  /**
   * Set the editor initiated
   * @param data - The editor initiated
   */
  const setEditorInitiated = (data: boolean) => {
    editorInitiated.value = data
  }
  /**
   * Set the editor
   * @param data - The editor
   */
  const setEditor = (data: Editor) => {
    editor.value = data
  }
  /**
   * Set the show style manager
   * @param data - The show style manager
   */
  const setShowStyleManager = (data: boolean) => {
    showStyleManager.value = data
  }
  /**
   * Set the show code editor style manager
   * @param data - The show code editor style manager
   */
  const setShowCodeEditorStyleManager = (data: boolean) => {
    showCodeEditorStyleManager.value = data
  }
  /**
   * Set the selected jdoodle style block
   * @param data - The selected JDOODLEBLOCKSNAMES
   */
  const setSelectedStyleJdoodleBlock = (data: keyof typeof JDOODLEBLOCKSNAMES | null) => {
    selectedStyleJdoodleBlock.value = data
  }
  /**
   * Set the selected style block
   * @param data - The selected style block
   */
  const setSelectedStyleBlock = (data: any) => {
    selectedStyleBlock.value = data
  }
  /**
   * Set the selected device
   * @param data - The selected device
   */
  const setSelectedDevice = (data: IDevice) => {
    selectedDevice.value = data
  }
  return {
    getIsEditorLoaded,
    setIsEditorLoaded,
    getCanEdit,
    setCanEdit,
    getAccountType,
    getOnEditMode,
    setOnEditMode,
    getIsNewEmbed,
    setIsNewEmbed,
    getEmbed,
    setEmbed,
    htmlRef,
    cssRef,
    jsRef,
    getEmbedConfig,
    getComponentSettings,
    addLanguageToEmbed,
    removeLanguageFromEmbed,
    getCurrentEmbedsTypeCount,
    setCurrentEmbedsTypeCount,
    getIsBasicType,
    getLanguages,
    getLanguageMeta,
    getSideMenuVisibility,
    setSideMenuVisibility,
    getSideMenuStageId,
    setSideMenuStageId,
    editor,
    getSelectedDevice,
    setSelectedDevice,
    editorConfig,
    getEditor,
    setEditor,
    getEditorInitiated,
    setEditorInitiated,
    getShowStyleManager,
    setShowStyleManager,
    getShowCodeEditorStyleManager,
    setShowCodeEditorStyleManager,
    getSelectedStyleJdoodleBlock,
    setSelectedStyleJdoodleBlock,
    getSelectedStyleBlock,
    setSelectedStyleBlock
  }
})
